import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled, { ThemeProvider } from 'styled-components';
import { useAuth } from '../contexts/auth/AuthContext';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

const theme = {
  colors: {
    primary: '#3A5E63',
    secondary: '#F3F3F3',
    text: '#344854',
    accent: '#E8F0F0',
    white: '#FFFFFF',
  },
  fonts: {
    main: "'Playfair Display', serif",
    body: "'Lato', sans-serif",
  },
  breakpoints: {
    mobile: '576px',
    tablet: '768px',
    desktop: '1024px',
    large: '1200px',
  },
  spacing: {
    xs: '0.5rem',
    sm: '1rem',
    md: '2rem',
    lg: '4rem',
    xl: '8rem',
  },
};

const NavbarContainer = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 50;
  background: rgba(255,255,255,0.95);
  backdrop-filter: blur(4px);
  box-sizing: border-box;

  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: 1rem 1.5rem;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;

  img {
    height: 55px;

    @media (max-width: ${theme.breakpoints.tablet}) {
      height: 45px;
    }
  }

  .logo-text {
    margin-left: 0.5rem;

    h1 {
      font-family: ${theme.fonts.main};
      color: ${theme.colors.primary};
      font-size: 1.5rem;
      margin: 0;
      font-weight: 600;

      @media (max-width: ${theme.breakpoints.tablet}) {
        font-size: 1.2rem;
      }
    }

    p {
      font-family: ${theme.fonts.body};
      color: ${theme.colors.primary};
      font-size: 0.8rem;
      margin: 0;

      @media (max-width: ${theme.breakpoints.tablet}) {
        font-size: 0.7rem;
      }
    }
  }
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  @media (max-width: ${theme.breakpoints.tablet}) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  color: ${theme.colors.primary};
  text-decoration: none;
  margin-left: 2rem;
  font-family: ${theme.fonts.body};
  font-size: 0.9rem;
  letter-spacing: 1px;
  transition: opacity 0.3s ease;
  &:hover {
    opacity: 0.8;
  }
`;

const LoginButton = styled(Link)`
  margin-left: 2rem;
  background-color: transparent;
  border: none;
  color: ${theme.colors.primary};
  cursor: pointer;
  display: flex;
  align-items: center;
  font-family: ${theme.fonts.body};
  svg {
    margin-right: 0.5rem;
  }
`;

const LogoutButton = styled.button`
  margin-left: 2rem;
  background-color: transparent;
  border: none;
  color: ${theme.colors.primary};
  cursor: pointer;
  display: flex;
  align-items: center;
  font-family: ${theme.fonts.body};
  font-size: 0.9rem;
  letter-spacing: 1px;
  svg {
    margin-right: 0.5rem;
  }
  &:hover {
    opacity: 0.8;
  }
`;

const AdminLink = styled(Link)`
  color: ${theme.colors.primary};
  background-color: ${theme.colors.accent};
  text-decoration: none;
  margin-left: 2rem;
  font-family: ${theme.fonts.body};
  font-size: 0.9rem;
  letter-spacing: 1px;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  transition: background-color 0.3s ease;
  &:hover {
    background-color: #d6e7e7;
  }
`;

const MobileNavLink = styled(Link)`
  color: ${theme.colors.primary};
  text-decoration: none;
  font-family: ${theme.fonts.body};
  font-size: 1.1rem;
  letter-spacing: 0.5px;
  padding: 0.75rem 0;
  transition: opacity 0.3s ease;
  width: 100%;
  display: block;
  border-bottom: 1px solid ${theme.colors.accent};

  &:hover {
    opacity: 0.7;
    background-color: ${theme.colors.accent};
    padding-left: 0.5rem;
  }
`;

const HamburgerButton = styled.button`
  display: none;
  @media (max-width: ${theme.breakpoints.tablet}) {
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    width: 28px;
    height: 24px;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0;
    z-index: 60;

    span {
      width: 28px;
      height: 3px;
      background: ${theme.colors.primary};
      border-radius: 2px;
      transition: all 0.3s ease;
      position: relative;
      transform-origin: 1px;
    }
  }
`;

const MobileMenuBackdrop = styled.div<{ $isOpen: boolean }>`
  display: none;
  @media (max-width: ${theme.breakpoints.tablet}) {
    display: block;
    position: fixed;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 55;
    transition: opacity 0.3s ease;
    opacity: ${props => props.$isOpen ? '1' : '0'};
    pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
  }
`;

const MobileMenuSidebar = styled.div<{ $isOpen: boolean }>`
  display: none;
  @media (max-width: ${theme.breakpoints.tablet}) {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 0;
    right: 0;
    height: 100vh;
    width: 80%;
    max-width: 300px;
    background-color: white;
    box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
    z-index: 60;
    transform: ${props => props.$isOpen ? 'translateX(0)' : 'translateX(100%)'};
    transition: transform 0.3s ease-in-out;
    overflow-y: auto;
  }
`;

const MobileSidebarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 1.25rem;
  border-bottom: 2px solid ${theme.colors.accent};
  flex-shrink: 0;
`;

const MobileSidebarNav = styled.nav`
  display: flex;
  flex-direction: column;
  padding: 1rem 1.25rem;
  flex: 1;
`;

const MobileLogoutButton = styled.button`
  font-family: ${theme.fonts.body};
  font-size: 1.1rem;
  color: ${theme.colors.primary};
  background: transparent;
  border: none;
  padding: 0.75rem 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  width: 100%;
  border-bottom: 1px solid ${theme.colors.accent};
  text-align: left;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 0.7;
    background-color: ${theme.colors.accent};
    padding-left: 0.5rem;
  }

  svg {
    margin-right: 0.5rem;
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 1.75rem;
  color: ${theme.colors.primary};
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 0.7;
  }
`;

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, isAuthenticated, isAdmin, isCrew, isCustomer, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  const handleLogout = async () => {
    await logout();
    navigate('/');
    setMobileOpen(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <NavbarContainer>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }} onClick={() => setMobileOpen(false)}>
          <Logo>
            <img src="/images/logo.png" alt="Healing Lotus Logo" />
            <div className="logo-text">
              <h1>HEALING LOTUS</h1>
              <p>Skin Spa & Blissful Med Spa</p>
            </div>
          </Logo>
        </Link>
        <NavLinks>
          <NavLink to="/about">{t('nav.about')}</NavLink>
          <NavLink to="/services">{t('nav.services')}</NavLink>
          <NavLink to="/gallery">{t('nav.gallery')}</NavLink>
          <NavLink to="/gift-card">{t('nav.giftCard')}</NavLink>
          <NavLink to="/contact">{t('nav.contact')}</NavLink>
          
          <LanguageSwitcher />

          {isAuthenticated ? (
            <>
              {isCustomer && (
                <NavLink to="/profile">Profile</NavLink>
              )}
              {isCrew && (
                <>
                  <NavLink to="/crew/dashboard">Crew Dashboard</NavLink>
                </>
              )}
              {isAdmin && (
                <AdminLink to="/admin/dashboard">{t('nav.adminPanel')}</AdminLink>
              )}
              <LogoutButton onClick={handleLogout}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 17L21 12L16 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {t('nav.logout')}
              </LogoutButton>
            </>
          ) : (
            <LoginButton to="/login">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" stroke="currentColor" strokeWidth="2"/>
                <path d="M20 21C20 17.13 16.36 14 12 14C7.64 14 4 17.13 4 21" stroke="currentColor" strokeWidth="2"/>
              </svg>
              {t('nav.login')}
            </LoginButton>
          )}
        </NavLinks>
        {/* Hamburger Icon for Mobile */}
        <HamburgerButton
          aria-label="Open menu"
          onClick={() => setMobileOpen(true)}
        >
          <span />
          <span />
          <span />
        </HamburgerButton>

        {/* Mobile Menu Backdrop */}
        <MobileMenuBackdrop
          $isOpen={mobileOpen}
          onClick={() => setMobileOpen(false)}
        />

        {/* Mobile Sidebar Menu */}
        <MobileMenuSidebar $isOpen={mobileOpen}>
          <MobileSidebarHeader>
            <div style={{ fontFamily: theme.fonts.main, fontSize: '1.25rem', color: theme.colors.primary, fontWeight: 600 }}>
              {t('nav.menu')}
            </div>
            <CloseButton onClick={() => setMobileOpen(false)} aria-label="Close menu">
              &#10005;
            </CloseButton>
          </MobileSidebarHeader>

          <MobileSidebarNav>
            <MobileNavLink to="/about" onClick={() => setMobileOpen(false)}>{t('nav.about')}</MobileNavLink>
            <MobileNavLink to="/services" onClick={() => setMobileOpen(false)}>{t('nav.services')}</MobileNavLink>
            <MobileNavLink to="/gallery" onClick={() => setMobileOpen(false)}>{t('nav.gallery')}</MobileNavLink>
            <MobileNavLink to="/gift-card" onClick={() => setMobileOpen(false)}>{t('nav.giftCard')}</MobileNavLink>
            <MobileNavLink to="/contact" onClick={() => setMobileOpen(false)}>{t('nav.contact')}</MobileNavLink>

            <LanguageSwitcher isMobile={true} />

            <div style={{ marginTop: '2rem', paddingTop: '1rem', borderTop: `1px solid ${theme.colors.accent}` }}>
              {isAuthenticated ? (
                <>
                  {isCustomer && (
                    <MobileNavLink to="/profile" onClick={() => setMobileOpen(false)}>
                      Profile
                    </MobileNavLink>
                  )}
                  {isCrew && (
                    <>
                      <MobileNavLink to="/crew/dashboard" onClick={() => setMobileOpen(false)}>
                        Crew Dashboard
                      </MobileNavLink>
                    </>
                  )}
                  {isAdmin && (
                    <MobileNavLink to="/admin/dashboard" onClick={() => setMobileOpen(false)}>
                      {t('nav.adminPanel')}
                    </MobileNavLink>
                  )}
                  <MobileLogoutButton onClick={handleLogout}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M16 17L21 12L16 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {t('nav.logout')}
                  </MobileLogoutButton>
                </>
              ) : (
                <MobileNavLink to="/login" onClick={() => setMobileOpen(false)}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '0.5rem' }}>
                      <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" stroke="currentColor" strokeWidth="2"/>
                      <path d="M20 21C20 17.13 16.36 14 12 14C7.64 14 4 17.13 4 21" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    {t('nav.login')}
                  </div>
                </MobileNavLink>
              )}
            </div>
          </MobileSidebarNav>
        </MobileMenuSidebar>
      </NavbarContainer>
    </ThemeProvider>
  );
};

export default Navbar; 